import { Info, ExternalLink, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { healthReferences, healthTopics, HEALTH_SOURCES_PATH, MEDICAL_DISCLAIMER } from "@/data/healthReferences";

export function HealthSourcesLink({ className = "", onClick }) {
  return <Link to={HEALTH_SOURCES_PATH} onClick={onClick}
    className={`inline-flex items-center gap-1.5 py-2 text-xs font-medium text-sky-300 underline underline-offset-4 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 ${className}`}>
    <BookOpen aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />Nutrition Information &amp; Sources
  </Link>;
}

export function HealthTopicContent({ topic, context }) {
  const content = healthTopics[topic];
  if (!content) return null;
  return <div className="space-y-4 text-sm leading-relaxed text-slate-300 normal-case tracking-normal font-normal">
    {context && <p className="rounded-xl bg-sky-400/10 p-3 text-sky-100">{context}</p>}
    <div className="space-y-3">{content.methodology.map(text => <p key={text}>{text}</p>)}</div>
    <p className="border-l-2 border-sky-400 pl-3"><strong className="text-white">Limits and review: </strong>{content.limitation}</p>
    <div className="space-y-3">
      <h3 className="font-semibold text-white">Scientific sources and scope</h3>
      {content.referenceIds.map(id => {
        const source = healthReferences[id];
        return <div key={id} className="rounded-xl border border-slate-700 p-3">
          {/* Capacitor 7 opens external target=_blank URLs via the OS browser.
              Real anchors also work on desktop, with no native dependency. */}
          <a href={source.url} target="_blank" rel="noopener noreferrer"
            className="inline text-sky-300 underline underline-offset-4 break-words rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400">
            {source.title}<ExternalLink className="ml-1 inline h-3.5 w-3.5" aria-hidden="true" />
            <span className="sr-only"> (opens in your browser in a new tab)</span>
          </a>
          <p className="mt-1 text-xs text-slate-400">{source.organization}{source.year ? ` (${source.year})` : ""}</p>
          <p className="mt-2 text-xs">{source.description}</p>
        </div>;
      })}
    </div>
  </div>;
}

export default function HealthSourceInfo({ topic, label, context, compact = false }) {
  const content = healthTopics[topic];
  if (!content) return null;
  return <Dialog>
    <DialogTrigger asChild>
      <button type="button" aria-label={label || content.title} title={label || content.title}
        onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}
        className={`inline-flex ${compact ? "h-6 w-6" : "h-8 w-8"} shrink-0 items-center justify-center rounded-full align-middle text-sky-300 hover:bg-sky-400/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400`}>
        <Info aria-hidden="true" className="h-3.5 w-3.5" />
      </button>
    </DialogTrigger>
    <DialogContent className="max-h-[calc(100dvh-3rem)] w-[calc(100vw-2rem)] sm:max-w-xl overflow-y-auto overscroll-contain border-slate-700 bg-[#020817] text-white text-left"
      onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}>
      <DialogHeader className="text-left pr-4">
        <DialogTitle>{content.title}</DialogTitle>
        <DialogDescription className="text-slate-300 leading-relaxed">{content.summary}</DialogDescription>
      </DialogHeader>
      <HealthTopicContent topic={topic} context={context} />
      <HealthSourcesLink />
      <p className="text-xs leading-relaxed text-slate-400">{MEDICAL_DISCLAIMER}</p>
    </DialogContent>
  </Dialog>;
}

// Compact entry point for forms, plan libraries and tracking views.
export function HealthSourceNote({ topic = "food", context }) {
  return <div className="flex flex-wrap items-center gap-x-1 text-xs text-slate-400 normal-case tracking-normal font-normal">
    <HealthSourceInfo topic={topic} context={context} />
    <HealthSourcesLink />
  </div>;
}
